import React from "react";
import { useForm, Link, Form } from "@inertiajs/react";
import { Button } from "@/components/ui/button";

interface UserSettings {
  email_address: string;
  company_name: string;
  company_address: string;
  company_phone: string;
  company_email: string;
  tax_id: string;
}

interface Props {
  user: UserSettings;
  errors?: Record<string, string[]>;
}

export default function Edit({ user, errors: serverErrors = {} }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    company_name: user.company_name || "",
    company_address: user.company_address || "",
    company_phone: user.company_phone || "",
    company_email: user.company_email || "",
    tax_id: user.tax_id || "",
  });

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="mt-2 text-gray-600">
          Manage your account and company information
        </p>
      </div>

      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Account Information
          </h2>
        </div>
        <div className="px-6 py-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <p className="text-gray-900">{user.email_address}</p>
            <p className="text-sm text-gray-500 mt-1">
              Contact support to change your email address
            </p>
          </div>
        </div>
      </div>

      <Form className="mt-6" action="/settings" method="put">
        <div className="bg-white shadow-sm rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Company Information
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              This information will be used as default values when creating new
              invoices
            </p>
          </div>

          <div className="px-6 py-4 space-y-6">
            <div>
              <label
                htmlFor="company_name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Company Name
              </label>
              <input
                type="text"
                id="company_name"
                name="user.company_name"
                defaultValue={user.company_name}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Company Inc."
              />
              {(errors.company_name || serverErrors.company_name) && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.company_name || serverErrors.company_name?.[0]}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="company_address"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Company Address
              </label>
              <textarea
                id="company_address"
                name="user.company_address"
                defaultValue={user.company_address}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="123 Main St&#10;Suite 100&#10;City, State 12345"
              />
              {(errors.company_address || serverErrors.company_address) && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.company_address || serverErrors.company_address?.[0]}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="company_phone"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Company Phone
                </label>
                <input
                  type="tel"
                  id="company_phone"
                  name="user.company_phone"
                  defaultValue={user.company_phone}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="(555) 123-4567"
                />
                {(errors.company_phone || serverErrors.company_phone) && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.company_phone || serverErrors.company_phone?.[0]}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="company_email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Company Email
                </label>
                <input
                  type="email"
                  id="company_email"
                  name="user.company_email"
                  defaultValue={user.company_email}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="billing@company.com"
                />
                {(errors.company_email || serverErrors.company_email) && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.company_email || serverErrors.company_email?.[0]}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="tax_id"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Tax ID / Business Number (Optional)
              </label>
              <input
                type="text"
                id="tax_id"
                name="user.tax_id"
                defaultValue={user.tax_id}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="XX-XXXXXXX"
              />
              {(errors.tax_id || serverErrors.tax_id) && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.tax_id || serverErrors.tax_id?.[0]}
                </p>
              )}
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
            <Button type="submit" disabled={processing}>
              {processing ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}
